import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MessageList, Message } from './MessageList';
import { MessageInput, FileAttachment } from './MessageInput';
import { TypingIndicator } from './TypingIndicator';
import { QuickResponses } from './QuickResponses';
import { CloseIcon, MenuDotsIcon, RestartIcon, EndChatIcon, SendIcon } from './icons';
import { MultiSelectMessage, MultiSelectOption } from './MultiSelectMessage';
import { ConfirmDialog } from './ConfirmDialog';
import { ImageModal } from './ImageModal';
import { WelcomeBanner } from './WelcomeBanner';
import { PoweredBy } from './PoweredBy';
import { formatTemplate } from './formatTemplate';

interface ChatWindowProps {
  account: string;
  apiKey: string;
  isOpen: boolean;
  // Inline in the host page and always open: a labelled region instead of a
  // dialog, with no close button and no Escape-to-close.
  embedded?: boolean;
  isConnected: boolean;
  isTyping: boolean;
  isSessionEnded: boolean;
  isUploading?: boolean;
  messages: Message[];
  quickResponses: string[];
  onClose: () => void;
  onSendMessage: (text: string, files?: FileAttachment[]) => void;
  onQuickResponse: (text: string) => void;
  onClearSession: () => void;
  onEndSession: () => void;
  onNavigate?: (url: string) => void;
  activityText?: string | null;
  uploadEnabled?: boolean;
  uploadAccept?: string | null;
  uploadMultiple?: boolean;
  uploadValidateError?: string | null;
  multiSelectOptions?: MultiSelectOption[];
  multiSelectMinSelect?: number;
  multiSelectMaxSelect?: number;
  multiSelectValidateErrorMessage?: string | null;
  onMultiSelectConfirm?: (selectedValues: string[]) => void;
  logoUrl?: string | null;
  botName?: string | null;
  labels?: Record<string, string>;
  inputPlaceholder?: string;
  inputFieldDescription?: string | null;
  inputFieldPlaceholder?: string | null;
  multiSelectDescription?: string | null;
  quickResponsesDescription?: string | null;
  uploadDescription?: string | null;
  offlineWarning?: string;
  sessionEndedMessage?: string;
  // BCP47 language of the chat content (from the backend config)
  lang?: string;
}

// Stable default for the multiSelectOptions prop. An inline `= []` default
// re-creates the array every render, which re-fires the selection-reset
// effect (deps: [multiSelectOptions]) whose setState causes the next render —
// an infinite loop whenever the prop is omitted (surfaced by act() in tests).
const NO_MULTI_SELECT_OPTIONS: MultiSelectOption[] = [];

/**
 * ChatWindow component
 * The main chat window that contains header, messages, and input
 */
export const ChatWindow: React.FC<ChatWindowProps> = ({
  account,
  apiKey,
  isOpen,
  embedded = false,
  isConnected,
  isTyping,
  isSessionEnded,
  isUploading = false,
  messages,
  quickResponses,
  onClose,
  onSendMessage,
  onQuickResponse,
  onClearSession,
  onEndSession,
  onNavigate,
  activityText,
  uploadEnabled = false,
  uploadAccept,
  uploadMultiple = false,
  uploadValidateError,
  logoUrl,
  botName,
  labels = {},
  multiSelectOptions = NO_MULTI_SELECT_OPTIONS,
  multiSelectMinSelect,
  multiSelectMaxSelect,
  multiSelectValidateErrorMessage,
  onMultiSelectConfirm,
  inputPlaceholder,
  inputFieldDescription = null,
  inputFieldPlaceholder = null,
  multiSelectDescription = null,
  quickResponsesDescription = null,
  uploadDescription = null,
  offlineWarning,
  sessionEndedMessage,
  lang,
}) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<FileAttachment[]>([]);
  const [wasConnected, setWasConnected] = useState(false);
  const [multiSelectSelected, setMultiSelectSelected] = useState<Set<string>>(new Set());
  const [multiSelectError, setMultiSelectError] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'restart' | 'end' | null>(null);
  const [selectedImage, setSelectedImage] = useState<{ src: string; alt: string } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const menuPopupRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);

  const handleAttachmentChange = useCallback((attachments: FileAttachment[]) => {
    setPendingAttachments(attachments);
  }, []);

  // Reset multi-select selection when options change (new multi-select or cleared)
  useEffect(() => {
    setMultiSelectSelected(new Set());
    setMultiSelectError(false);
  }, [multiSelectOptions]);

  const multiSelectActive = multiSelectOptions.length > 0 && onMultiSelectConfirm != null;

  const handleMultiSelectToggle = useCallback(
    (value: string) => {
      setMultiSelectSelected((prev) => {
        const next = new Set(prev);
        if (next.has(value)) {
          next.delete(value);
        } else {
          const effectiveMax =
            multiSelectMaxSelect != null && multiSelectMaxSelect > 0 ? multiSelectMaxSelect : 0;
          if (effectiveMax > 0 && next.size >= effectiveMax) {
            return prev;
          }
          next.add(value);
        }
        // Update input field with selected labels
        if (inputRef.current) {
          const selectedText = multiSelectOptions
            .filter((opt) => next.has(opt.value))
            .map((opt) => opt.label)
            .join(', ');
          inputRef.current.value = selectedText;
        }
        return next;
      });
      setMultiSelectError(false);
    },
    [multiSelectMaxSelect, multiSelectOptions],
  );

  // Scroll to bottom helper
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    }
  };

  // Track if connection was established at least once
  useEffect(() => {
    if (isConnected) {
      setWasConnected(true);
    }
  }, [isConnected]);

  // Remove the collapsed window from the tab order and accessibility tree.
  // The window is permanently mounted and hidden only via CSS visibility; the
  // inert attribute (set via ref — React 17 has no inert prop) also covers the
  // failure mode where the async-loaded stylesheet never arrives.
  useEffect(() => {
    const el = windowRef.current;
    if (!el) {
      return;
    }
    if (isOpen) {
      el.removeAttribute('inert');
      el.removeAttribute('aria-hidden');
    } else {
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
    }
  }, [isOpen]);

  /**
   * Escape closes the chat window (APG dialog pattern). Attached to the window
   * element — NOT document — so only Escape pressed while focus is inside the
   * widget closes it: the chat is a non-modal dialog embedded on customer
   * pages, and a document listener would hijack Escape aimed at host-page UI
   * (dropdowns, cookie banners) and yank focus back to the launcher. A dialog,
   * the lightbox, or the options menu consume Escape themselves. An embedded
   * window has nothing to close, so Escape does nothing there.
   *
   * @param e keyboard event bubbling from anywhere inside the window
   */
  const handleWindowKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Escape') {
      return;
    }
    if (embedded || confirmAction || selectedImage || isMenuOpen) {
      return;
    }
    onClose();
  };

  // APG menu pattern: focus moves into the menu when it opens.
  useEffect(() => {
    if (isMenuOpen) {
      menuPopupRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    }
  }, [isMenuOpen]);

  // Focus the input field when the chat window opens or becomes enabled.
  // Skip on mobile to avoid opening the software keyboard automatically, and
  // when embedded: it connects on page load, and focusing would steal focus
  // from the host page and scroll it to the chat.
  useEffect(() => {
    const disabled = !isConnected || isSessionEnded;
    const isMobile = window.matchMedia('(max-width: 480px)').matches;
    if (isOpen && inputRef.current && !disabled && !isMobile && !embedded) {
      inputRef.current.focus();
    }
  }, [isOpen, isConnected, isSessionEnded, embedded]);

  // Scroll to bottom when messages change or typing state changes
  useEffect(() => {
    if (isOpen) {
      setTimeout(scrollToBottom, 50);
    }
  }, [messages, isTyping, isOpen]);

  // When the input wrapper grows (e.g. a new inline description appears above
  // the bottom bar, an attachment preview is added, or a selection UI renders),
  // scroll messages to the bottom so the latest exchange stays visible.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const wrapper = inputWrapperRef.current;
    if (!wrapper || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(() => {
      scrollToBottom();
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [isOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !event.composedPath().includes(menuRef.current)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Mobile keyboard awareness: scroll messages to bottom when input is focused
  // so the most recent messages stay visible above the keyboard.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const input = inputRef.current;
    if (!input) {
      return;
    }

    const mql = window.matchMedia('(max-width: 480px)');

    const handleFocus = () => {
      if (!mql.matches) {
        return;
      }
      setTimeout(scrollToBottom, 300);
    };

    input.addEventListener('focus', handleFocus);

    return () => {
      input.removeEventListener('focus', handleFocus);
    };
  }, [isOpen]);

  // Keep close button visible when mobile keyboard opens.
  // iOS/Android pan the visual viewport when the keyboard appears, pushing
  // position:absolute/fixed elements out of view. Compensate by translating
  // the close button by visualViewport.offsetTop.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const mql = window.matchMedia('(max-width: 480px)');
    if (!mql.matches) {
      return;
    }

    const vv = window.visualViewport;
    const btn = closeBtnRef.current;
    if (!vv || !btn) {
      return;
    }

    let scheduled = false;

    const updatePosition = () => {
      scheduled = false;
      btn.style.transform = `translateY(${vv.offsetTop}px)`;
    };

    const scheduleUpdate = () => {
      if (scheduled) {
        return;
      }
      scheduled = true;
      requestAnimationFrame(updatePosition);
    };

    vv.addEventListener('resize', scheduleUpdate);
    vv.addEventListener('scroll', scheduleUpdate);

    return () => {
      vv.removeEventListener('resize', scheduleUpdate);
      vv.removeEventListener('scroll', scheduleUpdate);
      btn.style.transform = '';
    };
  }, [isOpen]);

  // Keep input wrapper anchored to keyboard top on mobile.
  // iOS visualViewport shrinks but the layout viewport does not, so the
  // input wrapper otherwise sits behind the keyboard. Translate it up by
  // the overlap between layout viewport and visual viewport.
  // Not when embedded: the window is in the page flow, not viewport-fixed, and
  // the browser scrolls the focused input into view itself.
  useEffect(() => {
    if (!isOpen || embedded) {
      return;
    }

    const mql = window.matchMedia('(max-width: 480px)');
    if (!mql.matches) {
      return;
    }

    const vv = window.visualViewport;
    const wrapper = inputWrapperRef.current;
    if (!vv || !wrapper) {
      return;
    }

    let scheduled = false;

    const updatePosition = () => {
      scheduled = false;
      const layoutHeight = window.innerHeight;
      const overlap = layoutHeight - vv.height - vv.offsetTop;
      const translate = overlap > 0 ? overlap : 0;
      wrapper.style.transform = `translateY(-${translate}px)`;
    };

    const scheduleUpdate = () => {
      if (scheduled) {
        return;
      }
      scheduled = true;
      requestAnimationFrame(updatePosition);
    };

    vv.addEventListener('resize', scheduleUpdate);
    vv.addEventListener('scroll', scheduleUpdate);
    // Initial run in case keyboard is already open
    scheduleUpdate();

    return () => {
      vv.removeEventListener('resize', scheduleUpdate);
      vv.removeEventListener('scroll', scheduleUpdate);
      wrapper.style.transform = '';
    };
  }, [isOpen, embedded]);

  const handleRestartChat = () => {
    setIsMenuOpen(false);
    setSelectedImage(null);
    setConfirmAction('restart');
  };

  const handleEndChat = () => {
    setIsMenuOpen(false);
    setSelectedImage(null);
    setConfirmAction('end');
  };

  const handleConfirm = () => {
    if (confirmAction === 'restart') {
      onClearSession();
    } else if (confirmAction === 'end') {
      onEndSession();
    }
    setConfirmAction(null);
  };

  const handleCancelConfirm = () => {
    setConfirmAction(null);
  };

  /**
   * APG menu keyboard behavior: ArrowUp/ArrowDown cycle with wrap, Home/End
   * jump, Escape and Tab close and refocus the trigger. Uses e.target because
   * document.activeElement returns the shadow host inside an open shadow root.
   *
   * @param e keyboard event from inside the menu popup
   */
  const handleMenuKeyDown = (e: React.KeyboardEvent) => {
    const items = Array.from(
      menuPopupRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? [],
    );
    if (items.length === 0) {
      return;
    }
    const currentIndex = items.indexOf(e.target as HTMLButtonElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      const next = (currentIndex + delta + items.length) % items.length;
      items[next].focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0].focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1].focus();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setIsMenuOpen(false);
      menuBtnRef.current?.focus();
    } else if (e.key === 'Tab') {
      // Refocus the trigger BEFORE the popup unmounts: React 17 flushes the
      // state update ahead of the browser's default Tab action, and a removed
      // activeElement would drop focus to <body> — Tab then restarts from the
      // top of the host page instead of the next control after the menu.
      menuBtnRef.current?.focus();
      setIsMenuOpen(false);
    }
  };

  /**
   * Closes the menu when focus leaves the menu container (e.g. Tab out or a
   * click that focuses elsewhere), so the popup never lingers open unseen.
   */
  const handleMenuBlur = (e: React.FocusEvent) => {
    if (menuRef.current && !menuRef.current.contains(e.relatedTarget as Node)) {
      setIsMenuOpen(false);
    }
  };

  const handleSendFromInput = () => {
    // Multi-select mode: send selected values via confirm callback
    if (multiSelectActive) {
      if (multiSelectSelected.size === 0) {
        return;
      }
      const effectiveMin =
        multiSelectMinSelect != null && multiSelectMinSelect > 0 ? multiSelectMinSelect : 0;
      const belowMin = effectiveMin > 0 && multiSelectSelected.size < effectiveMin;
      if (belowMin) {
        setMultiSelectError(true);
        return;
      }
      onMultiSelectConfirm!(Array.from(multiSelectSelected));
      setMultiSelectSelected(new Set());
      setMultiSelectError(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      scrollToBottom();
      return;
    }

    const text = inputRef.current?.value.trim() || '';
    if (text || pendingAttachments.length > 0) {
      onSendMessage(text, pendingAttachments.length > 0 ? pendingAttachments : undefined);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      setPendingAttachments([]);
      scrollToBottom();
    }
  };

  /**
   * Picks which label string belongs above the active interactive control.
   * Only one of these branches is active at a time. The upload case falls
   * back to the localized "Upload" label so the upload control has the same
   * label treatment (font, alignment, force-break) as every other prompt.
   */
  const resolvePromptLabel = (): string | null => {
    if (inputFieldDescription) return inputFieldDescription;
    if (multiSelectActive && multiSelectDescription) return multiSelectDescription;
    if (quickResponses.length > 0 && quickResponsesDescription) return quickResponsesDescription;
    if (uploadEnabled) return uploadDescription || labels.upload || 'Upload';
    return null;
  };
  const promptLabel = resolvePromptLabel();

  const isDisabled = !isConnected || isSessionEnded;

  // Localized min/max constraint sentence for the multi-select group
  const minSelect =
    multiSelectMinSelect != null && multiSelectMinSelect > 0 ? multiSelectMinSelect : 0;
  const maxSelect =
    multiSelectMaxSelect != null && multiSelectMaxSelect > 0 ? multiSelectMaxSelect : 0;
  let multiSelectConstraint: string | null = null;
  if (minSelect > 0 && maxSelect > 0) {
    multiSelectConstraint = formatTemplate(
      labels.ariaSelectBetween || 'Select between {0} and {1} options.',
      minSelect,
      maxSelect,
    );
  } else if (minSelect > 0) {
    multiSelectConstraint = formatTemplate(
      labels.ariaSelectAtLeast || 'Select at least {0} options.',
      minSelect,
    );
  } else if (maxSelect > 0) {
    multiSelectConstraint = formatTemplate(
      labels.ariaSelectUpTo || 'Select up to {0} options.',
      maxSelect,
    );
  }

  const windowName = botName || labels.ariaChatWindow || 'Chat';

  return (
    // Escape-on-the-dialog-container is the APG dialog keyboard pattern; the
    // jsx-a11y rule does not model role="dialog" as interactive.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      className="omq-chatbot-window"
      id="omq-chatbot-window"
      role={embedded ? 'region' : 'dialog'}
      aria-label={windowName}
      tabIndex={-1}
      ref={windowRef}
      onKeyDown={handleWindowKeyDown}>
      <h2 className="omq-sr-only">{windowName}</h2>
      {!embedded && (
        <button
          ref={closeBtnRef}
          className="omq-chatbot-close-btn"
          onClick={onClose}
          aria-label={labels.ariaCloseChat || 'Close chat'}
          type="button">
          <CloseIcon />
        </button>
      )}

      {/* Keyboard-scrollable transcript: the scrollbar is CSS-suppressed, so the
          scroll container needs a tab stop for keyboard-only (non-SR) users.
          eslint-disable: jsx-a11y flags tabIndex on non-interactive elements, but
          focusable scroll regions are the accepted exception (axe: scrollable-region-focusable). */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <div className="omq-chatbot-messages-container" ref={messagesContainerRef} tabIndex={0}>
        <div className="omq-chatbot-gradient-bg" aria-hidden="true">
          <WelcomeBanner logoUrl={logoUrl} botName={botName} />
        </div>
        <div className="omq-chatbot-top-fade" aria-hidden="true" />
        <MessageList
          messages={messages}
          onNavigate={onNavigate}
          onImageClick={setSelectedImage}
          ariaLabel={labels.ariaMessageList || 'Chat messages'}
          youLabel={labels.ariaYou}
          botLabel={botName || labels.ariaChatbot}
          snippetTitle={labels.ariaEmbeddedContent}
          lang={lang}
          continueTemplate={labels.continueTo}
          opensNewTabLabel={labels.ariaOpensNewTab}
        />
        {/* Always-mounted typing announcement: a live region inserted together
            with its content is not announced, so the region exists up front and
            only its text toggles. The TypingIndicator itself is purely visual. */}
        <span className="omq-sr-only" role="status" aria-live="polite">
          {isTyping ? activityText || labels.ariaAgentTyping || 'Agent is typing' : ''}
        </span>
        {isTyping && <TypingIndicator activityText={activityText} />}
      </div>

      {isSessionEnded && (
        <div className="omq-chatbot-session-ended" role="status">
          {sessionEndedMessage || 'Session ended'}
        </div>
      )}

      {!isConnected && wasConnected && !isSessionEnded && (
        <div className="omq-chatbot-offline-warning" role="alert">
          {offlineWarning || 'Connection lost.'}
        </div>
      )}

      <div className="omq-chatbot-input-wrapper" ref={inputWrapperRef}>
        <div
          className="omq-chatbot-prompt-description"
          id="omq-chatbot-prompt"
          role="status"
          aria-live="polite"
          dangerouslySetInnerHTML={{ __html: promptLabel || '' }}
        />
        {quickResponses.length > 0 && (
          <QuickResponses
            responses={quickResponses}
            ariaLabel={labels.ariaQuickResponses || 'Quick responses'}
            describedBy="omq-chatbot-prompt"
            onSelect={(text) => {
              onQuickResponse(text);
              // Desktop: move focus to the input. Mobile: the input would pop
              // the software keyboard, so park focus on the window container
              // (the clicked button is about to unmount).
              if (!window.matchMedia('(max-width: 480px)').matches) {
                inputRef.current?.focus();
              } else {
                // preventScroll when embedded: the in-flow window can be taller than
                // the viewport, and focusing it would jump the page to its top.
                windowRef.current?.focus(embedded ? { preventScroll: true } : undefined);
              }
            }}
          />
        )}
        {multiSelectActive && (
          <MultiSelectMessage
            options={multiSelectOptions}
            selected={multiSelectSelected}
            onToggle={handleMultiSelectToggle}
            disabled={isDisabled}
            maxSelect={multiSelectMaxSelect}
            validateErrorMessage={multiSelectValidateErrorMessage}
            showError={multiSelectError}
            ariaLabel={labels.ariaMultiSelect || 'Select options'}
            constraintText={multiSelectConstraint}
            maxReachedText={labels.ariaMaxSelectionReached}
            describedBy="omq-chatbot-prompt"
          />
        )}
        {uploadEnabled && (
          <MessageInput
            onSend={onSendMessage}
            onAttachmentChange={handleAttachmentChange}
            disabled={isDisabled}
            isUploading={isUploading}
            uploadAccept={uploadAccept}
            uploadMultiple={uploadMultiple}
            uploadValidateError={uploadValidateError}
            ariaUploadFile={labels.ariaUploadFile || 'Upload file'}
            ariaRemoveAttachment={labels.ariaRemoveAttachment || 'Remove attachment'}
            ariaUploading={labels.ariaUploading}
            ariaAddMoreFiles={labels.ariaAddMoreFiles}
            describedBy="omq-chatbot-prompt"
          />
        )}
        <div className="omq-chatbot-bottom-bar">
          <div className="omq-chatbot-menu-container" ref={menuRef} onBlur={handleMenuBlur}>
            <button
              ref={menuBtnRef}
              className="omq-chatbot-menu-btn"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={labels.ariaChatOptions || 'Chat options'}
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              aria-controls="omq-chatbot-menu"
              type="button">
              <MenuDotsIcon />
            </button>
            {isMenuOpen && (
              <div
                className="omq-chatbot-menu-popup"
                role="menu"
                id="omq-chatbot-menu"
                ref={menuPopupRef}
                aria-label={labels.ariaChatOptions || 'Chat options'}
                tabIndex={-1}
                onKeyDown={handleMenuKeyDown}>
                <button
                  className="omq-chatbot-menu-item"
                  onClick={handleRestartChat}
                  type="button"
                  role="menuitem"
                  tabIndex={-1}>
                  <RestartIcon />
                  <span>{labels.restartChat || 'Restart chat'}</span>
                </button>
                <button
                  className="omq-chatbot-menu-item"
                  onClick={handleEndChat}
                  type="button"
                  role="menuitem"
                  tabIndex={-1}>
                  <EndChatIcon />
                  <span>{labels.endChat || 'End chat'}</span>
                </button>
              </div>
            )}
          </div>
          <input
            ref={inputRef}
            type="text"
            className="omq-chatbot-input"
            placeholder={inputFieldPlaceholder || inputPlaceholder || 'Type your message...'}
            disabled={isDisabled}
            readOnly={multiSelectSelected.size > 0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isDisabled) {
                handleSendFromInput();
              }
            }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            name="omq-chatbot-message"
            data-lpignore="true"
            data-form-type="other"
            data-1p-ignore="true"
            aria-label={labels.ariaTypeMessage || 'Type your message'}
            aria-describedby={
              multiSelectActive && multiSelectSelected.size > 0
                ? 'omq-chatbot-prompt omq-chatbot-selection-note'
                : 'omq-chatbot-prompt'
            }
          />
          {multiSelectActive && (
            <span id="omq-chatbot-selection-note" className="omq-sr-only">
              {labels.ariaSelectionSummary || 'Shows your current selection'}
            </span>
          )}
          <button
            className="omq-chatbot-send-btn"
            disabled={isDisabled}
            onClick={handleSendFromInput}
            type="button"
            aria-label={
              multiSelectActive
                ? labels.ariaConfirmSelection || 'Confirm selection'
                : labels.ariaSendMessage || 'Send message'
            }>
            <SendIcon />
          </button>
        </div>
      </div>
      <PoweredBy
        account={account}
        apiKey={apiKey}
        template={labels.poweredBy || '{0} Chatbot'}
        opensNewTabLabel={labels.ariaOpensNewTab}
      />
      {confirmAction && (
        <ConfirmDialog
          message={
            confirmAction === 'restart'
              ? labels.confirmRestart || 'Are you sure you want to restart the chat?'
              : labels.confirmEnd || 'Are you sure you want to end the chat?'
          }
          confirmLabel={
            confirmAction === 'restart' ? labels.restart || 'Restart' : labels.endChat || 'End chat'
          }
          cancelLabel={labels.cancel || 'Cancel'}
          onConfirm={handleConfirm}
          onCancel={handleCancelConfirm}
          returnFocusEl={menuBtnRef.current}
        />
      )}
      {selectedImage && (
        <ImageModal
          src={selectedImage.src}
          alt={selectedImage.alt}
          onClose={() => setSelectedImage(null)}
          ariaLabel={labels.ariaImagePreview || 'Image preview'}
          ariaCloseLabel={labels.ariaCloseImage || 'Close image'}
          altFallback={labels.ariaImageFromChat}
        />
      )}
    </div>
  );
};
